import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlayIcon, SearchIcon, SendIcon } from 'lucide-react';
import { Section } from '../Section';
import { InView } from '../InView';
import { SectionHeadline } from '../SectionHeadline';
import { SnakStill } from '../../SnakStill';
import { featuredSnak, shelfSnaks, shelfThumb, stationFilters, stationPillars, stationScreenshot, stationSource } from '../../../data/deck';
import type { ShelfSnak, StationFilter } from '../../../data/deck';
export function StationSection() {
  const [filter, setFilter] = useState<StationFilter>('All');
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return shelfSnaks.filter(snak => (filter === 'All' || snak.category === filter) && (!q || `${snak.title} ${snak.category}`.toLowerCase().includes(q)));
  }, [filter, query]);
  return <Section id="station" data-id="element-613" data-mp="2nzdh9th3osb7">
      <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16" data-id="element-614" data-mp="3prz07smrskvp">
        <InView data-id="element-615" data-mp="3tsr9glvn6prz">
          <SectionHeadline lead="Every Snak on one shelf." follow="Every market picks what works." data-id="element-616" data-mp="3ts9ertomojm6" />
        </InView>
        <InView delay={0.1} data-id="element-617" data-mp="2o178rjzbu4b">
          <p className="text-[14.5px] leading-[1.7] text-[var(--muted)]" data-id="element-618" data-mp="2kn43h3hrgvt">
            Snakker Station stores every Snak in one place, so any Ferring country can see what is working elsewhere — then
            preview, reuse and localize it instead of starting over.
          </p>
        </InView>
      </div>

      <InView delay={0.16} y={26} className="mt-12" data-id="element-619" data-mp="3b06tg511kf5l">
        <div className="overflow-hidden rounded-[30px] bg-white shadow-[0_50px_90px_-60px_rgba(14,27,51,0.6)] ring-1 ring-[var(--fg)]/[0.08]" data-id="element-620" data-mp="3g0ujoohg4ryr">
          <div className="p-5 sm:p-7" data-id="element-621" data-mp="3ku5vl4147yxj">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-6" data-id="element-622" data-mp="2ndqlp3grtdwh">
              <div className="flex shrink-0 items-center gap-3" data-id="element-623" data-mp="2sbib9qty0nxi">
                <span className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-[var(--brand)] text-white" data-id="element-624" data-mp="2s9p3u9bi9zog">
                  <PlayIcon className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-625" data-mp="2ga0i8p4nzt5v" />
                </span>
                <span className="text-[24px] font-extrabold leading-none tracking-[-0.03em] text-[var(--fg)]" data-id="element-626" data-mp="2u1ov4umqmlnw">Snakker Station</span>
              </div>
              <label className="flex flex-1 items-center gap-2.5 rounded-full bg-[var(--surface)] px-4 py-2.5 focus-within:ring-2 focus-within:ring-[var(--brand)]" data-id="element-627" data-mp="2p2udvx4f555w">
                <SearchIcon className="h-4 w-4 text-[var(--fg)]/45" aria-hidden="true" data-id="element-628" data-mp="1xby1syq6n4jo" />
                <span className="sr-only" data-id="element-629" data-mp="2baxqg6dp3jfu">Search Snaks</span>
                <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search — try “congress” or “follow-up”" className="w-full bg-transparent text-[13px] text-[var(--fg)] placeholder:text-[var(--fg)]/40 focus:outline-none" data-id="element-630" data-mp="2b8b7zbp9njiu" />
              </label>
              <span className="shrink-0 text-[12.5px] font-bold text-[var(--brand)]" data-id="element-631" data-mp="13l35qk4gdz8b">
                Discover. Preview. Reuse. Localize. Activate.
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2" data-id="element-632" data-mp="uk4zdv6jlfmk">
              {stationFilters.map(item => <button key={item} type="button" onClick={() => setFilter(item)} aria-pressed={filter === item} className={['rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]', filter === item ? 'bg-[var(--fg)] text-white' : 'bg-[var(--surface)] text-[var(--fg)] hover:bg-[#EBE6DB]'].join(' ')} data-id="element-633" data-mp="zj86dl631vx6">
                  {item}
                </button>)}
              <span className="ml-auto hidden text-[11.5px] text-[var(--muted)] md:inline" data-id="element-634" data-mp="mahg44g86eze">
                {shelfSnaks.length + 1} Snaks · curated for global and local teams
              </span>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-[240px_minmax(0,0.8fr)_minmax(0,1.6fr)]" data-id="element-635" data-mp="d99g9hy29637">
              <div className="flex items-center justify-center rounded-[22px] bg-[#EFEBE2] px-4 py-5" data-id="element-636" data-mp="8bq3kmh0h1dd">
                <SnakStill screen={0} alt="The REKOVELLE teaser Snak: two patients — same approach?" className="h-[380px] w-[196px]" data-id="element-637" data-mp="151gxokle4min" />
              </div>

              <div className="flex flex-col" data-id="element-638" data-mp="w7c9ya1jzr7c">
                <span className="w-fit rounded-full bg-[var(--brand)]/10 px-3 py-1 text-[11px] font-bold text-[var(--brand)]" data-id="element-639" data-mp="ra2cq87xab8y">
                  {featuredSnak.category}
                </span>
                <h3 className="mt-3 text-[24px] font-extrabold leading-[1.05] tracking-[-0.03em] text-[var(--fg)]" data-id="element-640" data-mp="1jyhrqur20tka">
                  {featuredSnak.title}
                </h3>
                <p className="mt-1.5 text-[12.5px] text-[var(--muted)]" data-id="element-641" data-mp="1rbqq1cs9rxi3">{featuredSnak.usedIn}</p>
                <span className="mt-6 text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-642" data-mp="1w8z71a1olvhr">Helps solve</span>
                <p className="mt-1.5 text-[18px] font-bold leading-[1.25] text-[var(--fg)]" data-id="element-643" data-mp="259fbomu5v8o7">{featuredSnak.solves}</p>
                <div className="mt-4 flex flex-wrap gap-1.5" data-id="element-644" data-mp="2a90bt0l2hn2i">
                  {featuredSnak.channels.map(channel => <span key={channel} className="rounded-full border border-[var(--fg)]/20 px-3 py-1 text-[11.5px] font-medium text-[var(--fg)]" data-id="element-645" data-mp="2f6a8906viyzu">
                      {channel}
                    </span>)}
                </div>
                <span className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-[var(--fg)] px-4 py-2.5 text-[12.5px] font-bold text-white lg:mt-auto" data-id="element-646" data-mp="2t0krj203q0yi">
                  <SendIcon className="h-3.5 w-3.5 text-[var(--brand-lighter)]" aria-hidden="true" data-id="element-647" data-mp="xvbi59r8y8za" />
                  Request adaptation
                </span>
              </div>

              <div data-id="element-648" data-mp="17lcy1e9g4l34">
                <p className="text-[16px] font-extrabold text-[var(--fg)]" data-id="element-649" data-mp="17lwgcw30dbho">
                  More on the shelf <span className="text-[var(--fg)]/40" data-id="element-650" data-mp="12ueiwi071t1g">· {visible.length}</span>
                </p>
                <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-651" data-mp="1hjau2nxjrm6a">
                  <AnimatePresence mode="popLayout" data-id="element-652" data-mp="1ckpvsu1uvckh">
                    {visible.map(snak => <motion.li key={snak.id} layout initial={{
                    opacity: 0,
                    scale: 0.96
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.96
                  }} transition={{
                    duration: 0.25
                  }} data-id="element-653" data-mp="1qkilqn5k5ore">
                        <ShelfThumb snak={snak} data-id="element-654" data-mp="3neloqx2y9siq" />
                        <p className="mt-2 text-[13px] font-bold leading-tight text-[var(--fg)]" data-id="element-655" data-mp="22u87dx65f318">{snak.title}</p>
                        <p className="mt-0.5 text-[11px] text-[var(--muted)]" data-id="element-656" data-mp="27u2zu55ko0oe">{snak.category}</p>
                      </motion.li>)}
                  </AnimatePresence>
                </ul>
                {visible.length === 0 && <p className="mt-6 text-[13px] text-[var(--muted)]" data-id="element-657" data-mp="1y1nf502w4xzt">No Snaks match that yet — try another filter.</p>}
              </div>
            </div>
          </div>

          <div className="grid gap-6 bg-[var(--fg)] px-5 py-7 sm:px-7 lg:grid-cols-[1fr_1fr_1fr_1.1fr] lg:items-center" data-id="element-658" data-mp="1p18rhu2gf6t5">
            {stationPillars.map(pillar => <div key={pillar.label} data-id="element-659" data-mp="1f5xbl8fb7tjt">
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--brand-lighter)]" data-id="element-660" data-mp="1f6yyqb9lo6hw">{pillar.label}</span>
                <p className="mt-1.5 text-[13px] leading-relaxed text-white/80" data-id="element-661" data-mp="39jot0u3vrhpt">{pillar.text}</p>
              </div>)}
            <p className="text-[19px] font-extrabold leading-[1.2] tracking-[-0.02em] text-white lg:text-right" data-id="element-662" data-mp="1x8idfxdwn2z9">
              One shared platform. Central intelligence. Local empowerment.
            </p>
          </div>
        </div>
      </InView>
    </Section>;
}
function ShelfThumb({
  snak
}: {
  snak: ShelfSnak;
}) {
  return <div className="relative overflow-hidden rounded-[12px] bg-[var(--surface)]" style={{
    aspectRatio: `${shelfThumb.width} / ${shelfThumb.height}`
  }} data-id="element-663" data-mp="1iiuiddwbh0jz">
      <img src={stationScreenshot} alt={`${snak.title} — ${snak.category} Snak thumbnail`} className="absolute block max-w-none" style={{
      width: `${stationSource.width / shelfThumb.width * 100}%`,
      left: `-${snak.x / shelfThumb.width * 100}%`,
      top: `-${snak.y / shelfThumb.height * 100}%`
    }} data-id="element-664" data-mp="19inz1bdxt6cw" />
    </div>;
}